import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Hero from './components/Hero';
import Services from './components/Services';
import PainPoints from './components/PainPoints';
import Pricing from './components/Pricing';
import ITSupport from './components/ITSupport';
import OneStopBanner from './components/OneStopBanner';
import About from './components/About';
import FAQ from './components/FAQ';
import Contact from './components/Contact';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // pricingページなどからstateで scrollTo が渡された場合にスクロール
  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 150);
      }
    }
  }, [location.state]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="https://static.readdy.ai/image/f9c7a126b558a9be781ef4c29ae5dafc/8ebc0db39f9829cf06d07a5266fdf192.png" alt="Zenwest Logo" className="h-10 w-10 object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-[#1A2332]">Zenwest</h1>
                <p className="text-xs text-[#0EA5E9]">会計＆ITサポート</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('services')} className="text-[#1A2332] hover:text-[#0EA5E9] transition-colors cursor-pointer whitespace-nowrap">サービス</button>
              <button onClick={() => scrollToSection('pricing')} className="text-[#1A2332] hover:text-[#0EA5E9] transition-colors cursor-pointer whitespace-nowrap">料金</button>
              <button onClick={() => scrollToSection('one-stop')} className="text-[#1A2332] hover:text-[#0EA5E9] transition-colors cursor-pointer whitespace-nowrap">強み</button>
              <Link to="/company" className="text-[#1A2332] hover:text-[#0EA5E9] transition-colors cursor-pointer whitespace-nowrap">事業所概要</Link>
              <button onClick={() => scrollToSection('contact')} className="text-[#1A2332] hover:text-[#0EA5E9] transition-colors cursor-pointer whitespace-nowrap">お問い合わせ</button>
            </div>

            <button onClick={() => scrollToSection('contact')} className="bg-[#0EA5E9] text-white px-6 py-2.5 rounded-full hover:bg-[#1A2332] transition-all duration-300 cursor-pointer whitespace-nowrap">
              無料相談予約
            </button>
          </div>
        </div>
      </nav>

      {/* Sections */}
      <Hero />
      <PainPoints />
      <Services />
      <Pricing />
      <ITSupport />
      <OneStopBanner onContact={() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }} />
      <About />
      <FAQ />
      <Contact />

      {/* Footer */}
      <footer className="bg-[#1A2332] text-white py-16 px-6 mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="https://static.readdy.ai/image/f9c7a126b558a9be781ef4c29ae5dafc/8ebc0db39f9829cf06d07a5266fdf192.png" alt="Zenwest Logo" className="h-8 w-8 object-contain" />
                <div>
                  <h3 className="text-xl font-bold">Zenwest</h3>
                  <p className="text-xs text-gray-400">会計＆ITサポート</p>
                </div>
              </div>
              <Link to="/company" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#0EA5E9] transition-colors cursor-pointer mt-2">
                <i className="ri-building-line text-xs"></i>
                事業所概要
              </Link>
            </div>

            <div>
              <h4 className="font-semibold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors cursor-pointer">記帳代行</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors cursor-pointer">IT導入支援</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors cursor-pointer">ホームページ作成</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors cursor-pointer">料金プラン</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors cursor-pointer">お問い合わせ</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">ご相談・お問い合わせ</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to="/company" className="hover:text-white transition-colors cursor-pointer">事業所概要</Link>
                </li>
                <li>
                  <button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors cursor-pointer">よくある質問</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors cursor-pointer">無料相談を予約する</button>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-700 text-center text-sm text-gray-400">
            <p>&copy; 2025 Zenwest. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}